/* eslint-disable eqeqeq */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Phase 6 Vendor-Port W5 — COIL CMS-PATH RESOLVER (§33.2, §31).
 *
 * ★ REUSE SEAM (uvcCms.ts §32): the universal CMS-doc accessor
 * `getFromCmsData(doc, dot-path, id)` — a verbatim port of legacy
 * `fn.tools.get_from_cms_data` — lives in uvcCms.ts. This module IMPORTS it and
 * adds the COIL doc path-map + label rules (same as energyWheelCms/phxCms did).
 *
 * The coil CMS doc (`GET /api/v1.0/cms/1/v1.0.0/ahu/coils/params/`, legacy
 * model.coil.cms urlRoot = `fn['current.coil.cms']`) carries:
 *   - `coil.rating_mfg`     — the MANUFACTURER list (id, name, external_rating).
 *                             THE external-vendor-vs-KSDM-DB gate per mfg
 *                             (coil.selector.js:604 + 1563). external_rating:true
 *                             ⇒ POST /api/v1.0/rating/; false ⇒ POST
 *                             /ahu/rating_database_coil_selection/ ('manual').
 *   - `coil.htfluid`        — the HT-fluid list (id, name, value, duties[]).
 *   - `coil.evaporator_mode`— the evaporator-mode list.
 *   - `air.air_stream`      — air-stream list (shared with the section CMS).
 *
 * The doc is WRAPPED under the partName key {"coil": {...}, "air": {...}}; the
 * paths INCLUDE the prefix but we tolerate an already-unwrapped doc (the form's
 * componentCms is unwrapped to its `coil` subtree) by stripping the leading seg.
 */

import { getFromCmsData, type UvcCmsDoc } from './uvcCms';
import type { CmsNamedRow } from '../cmsDoc';

export type CoilCmsDoc = UvcCmsDoc;

export const COIL_CMS_DOC_URL = '/api/v1.0/cms/1/v1.0.0/ahu/coils/params/';
/** The AHU params doc — carries `air.air_stream` (the coil CMS doc does NOT;
 * legacy reads air streams off AHUCms / the tunnel CMS, not the coil doc). */
export const AHU_CMS_DOC_URL = '/api/v1.0/cms/1/v1.0.0/ahu/params/';

/** Resolve a dot-path against a doc that may be wrapped OR unwrapped. */
function resolvePath(doc: CoilCmsDoc | undefined, path: string): string {
  if (!doc) return path;
  const head = path.split('.')[0];
  if (head in (doc as Record<string, unknown>)) return path;
  // unwrapped doc (already the `coil` subtree) → strip the leading segment.
  if (head === 'coil' || head === 'air') return path.split('.').slice(1).join('.');
  return path;
}

/** Walk to a doc array at a dot-path (or [] when absent). */
function docArray(doc: CoilCmsDoc | undefined, path: string): any[] {
  const res = getFromCmsData(doc, resolvePath(doc, path), null);
  return Array.isArray(res) ? res : res ? [res] : [];
}

export interface CoilRatingMfg {
  id: number;
  name: string;
  external_rating: boolean;
  [k: string]: unknown;
}

/** The MANUFACTURER list from coil.rating_mfg (id, name, external_rating). */
export function coilRatingMfgs(doc: CoilCmsDoc | undefined): CoilRatingMfg[] {
  return docArray(doc, 'coil.rating_mfg').map((r) => ({
    id: Number(r.id),
    name: String(r.name ?? ''),
    external_rating: Boolean(r.external_rating),
    ...r,
  }));
}

/** external_rating boolean for an mfg id (the rate-path gate, §33.2). undefined
 * when the mfg isn't in the rating_mfg list. */
export function coilExternalRating(
  doc: CoilCmsDoc | undefined,
  mfgId: number | string | null | undefined
): boolean | undefined {
  if (mfgId == null) return undefined;
  const row = coilRatingMfgs(doc).find((m) => m.id == Number(mfgId));
  return row?.external_rating;
}

/** mfg display name for an id. */
export function coilMfgName(
  doc: CoilCmsDoc | undefined,
  mfgId: number | string | null | undefined
): string {
  const row = coilRatingMfgs(doc).find((m) => m.id == Number(mfgId));
  return row?.name ?? '';
}

/** The HT-fluid list, optionally filtered to the rows whose duties[] include
 * the current duty_id (legacy htfluid select filter). */
export function coilHtfluids(
  doc: CoilCmsDoc | undefined,
  dutyId?: number | string | null
): CmsNamedRow[] {
  const all = docArray(doc, 'coil.htfluid') as CmsNamedRow[];
  if (dutyId == null) return all;
  return all.filter((r) =>
    ((r as any).duties ?? []).some((d: any) => d.id == dutyId)
  );
}

/** The evaporator-mode list. */
export function coilEvaporatorModes(doc: CoilCmsDoc | undefined): CmsNamedRow[] {
  return docArray(doc, 'coil.evaporator_mode') as CmsNamedRow[];
}

/** The air-stream list — read from the AHU params doc (`air.air_stream`), NOT
 * the coil CMS doc (which has no `air` key; legacy reads it off the tunnel/AHU
 * CMS). Pass the AHU params doc here. */
export function coilAirStreams(ahuDoc: CoilCmsDoc | undefined): CmsNamedRow[] {
  return docArray(ahuDoc, 'air.air_stream') as CmsNamedRow[];
}
